import { useCallback, useMemo, useState } from 'react';
import type { ImportResult } from '../lib/api';
import {
  FRONT_DOOR_STEP_IDS,
  TOTAL_FRONT_DOOR_STEPS,
  buildWorkRightsPatch,
  clampFrontDoorStepIndex,
  deriveSuggestedPreferredName,
  deriveTargetGeoIds,
  resolveRelocationPostures,
  resolveWorkRightsSelection,
  type FrontDoorStepId,
} from '../lib/frontDoorCards';
import { useAuth } from '../lib/useAuth';
import { useFrontDoorCards } from '../lib/useFrontDoorCards';
import { describeResult } from './ImportPasteCard';
import {
  ContactCard,
  FinaleCard,
  HomeCountryCard,
  PostalRadiusCard,
  PreferredNameCard,
  RelocationCard,
  StepFrame,
  WorkRightsCard,
} from './frontDoorCardBodies';
import styles from './FrontDoorCards.module.css';

export interface FrontDoorCardsProps {
  /** His test path (?onboarding=preview) — every answer still saves for
   *  real, only the final onboarded_at stamp is withheld. See
   *  lib/frontDoorCards.ts's isOnboardingPreview doc comment. */
  preview: boolean;
  /** Fires once, when the whole 7-card phase retires (finished the finale,
   *  OR the topRow's skip-all) — App.tsx wires this to useFrontDoorGate's
   *  markFinished, which is what lets homeRouteGate/App.tsx's tab bar/coach
   *  mark move on to the existing arc. */
  onDone: () => void;
  /** Fires the moment the finale's own import (via ImportPasteCard, "the
   *  arc's own corpus entry") actually succeeds — App.tsx wires this to the
   *  EXISTING useOnboardingGate's own refreshCorpus, so by the time this
   *  phase hands off, the existing arc's corpus-truth check
   *  (resolveOnboardingStep's roleCount>0 rule) already knows a real corpus
   *  exists and skips straight to watchlist-seed instead of re-asking via
   *  demo/one-question. See this file's own header comment below for the
   *  full integration rationale. */
  onCorpusSeeded: () => void;
}

/**
 * E7 FRONT DOOR — the 7-card guided first-login flow, composed as the
 * LEADING SEGMENT of the existing onboarding arc (docs/diagrams/
 * c4-e7-front-door-2026-07-17.html; docs/design/first-login-profile-setup-
 * 2026-07-09.md). Rendered by App.tsx into the SAME `overlay` slot the
 * existing corpus-discovery <Onboarding> component uses — never both at
 * once (App.tsx's own composition picks exactly one, cards first).
 *
 * THE INTEGRATION (not a parallel flow): once this phase's finale seeds the
 * corpus (or the user explicitly skips it), control simply passes back to
 * App.tsx, which is ALREADY running the existing arc's own useOnboardingGate
 * underneath, unmodified. That gate's resolveOnboardingStep has ALWAYS had
 * its own honest rule — "roleCount > 0 -> skip straight to watchlist-seed,
 * never re-show demo/one-question" (onboardingStep.ts) — this phase does not
 * change that rule at all; it only makes sure the existing gate's corpus
 * read is FRESH by the time control passes, via onCorpusSeeded (App.tsx's
 * gate.refreshCorpus). A user who skips the finale without importing
 * anything simply lands on the existing arc's own 'demo' step as it always
 * has — a second, gentler on-ramp, never a dead end.
 *
 * Deliberately does NOT touch onboardingStep.ts/Onboarding.tsx/
 * useOnboardingGate.ts — those files' 5-node sequence is diagram-pinned
 * (onboardingStep.test.ts's own "matches the c3-onboarding-2026-07-04.svg
 * node order exactly" case) and this lane's brief is a port + compose, not a
 * re-shape of an already-shipped, already-tested arc.
 */
export function FrontDoorCards({ preview, onDone, onCorpusSeeded }: FrontDoorCardsProps) {
  const { user } = useAuth();
  const { state, loading, loadError, saving, saveError, saveCard, complete } = useFrontDoorCards();

  const [stepIndex, setStepIndex] = useState(0);
  // Client-side-only, this-session memory of which cards the user skipped —
  // mirrors apps/web's FrontDoor.tsx exactly (there is no server column for
  // "was this skipped," only whether the field is null, which idle and
  // skipped share honestly).
  const [skippedSteps, setSkippedSteps] = useState<ReadonlySet<FrontDoorStepId>>(() => new Set());
  const [finaleSeeded, setFinaleSeeded] = useState(false);
  const [finaleResultNote, setFinaleResultNote] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);

  const advance = useCallback(() => setStepIndex((i) => clampFrontDoorStepIndex(i + 1)), []);
  const goBack = useCallback(() => setStepIndex((i) => clampFrontDoorStepIndex(i - 1)), []);
  const skipStep = useCallback(
    (id: FrontDoorStepId) => {
      setSkippedSteps((prev) => new Set(prev).add(id));
      advance();
    },
    [advance],
  );

  const finishFlow = useCallback(async () => {
    setFinishing(true);
    try {
      // NEVER called from preview (skip-all or the finale's "Let's go") —
      // preview's whole point is to walk the flow without stamping. See
      // lib/frontDoorCards.ts's isOnboardingPreview doc comment.
      if (!preview) await complete();
      onDone();
    } finally {
      setFinishing(false);
    }
  }, [preview, complete, onDone]);

  const skipAll = useCallback(() => {
    void finishFlow();
  }, [finishFlow]);

  const handleFinaleImported = useCallback(
    (result: ImportResult) => {
      setFinaleResultNote(describeResult(result));
      setFinaleSeeded(true);
      onCorpusSeeded();
    },
    [onCorpusSeeded],
  );

  const suggestedPreferredName = useMemo(
    () => deriveSuggestedPreferredName(user?.displayName ?? null),
    [user?.displayName],
  );

  if (loading) {
    return <div className={styles.overlay} />;
  }

  if (loadError || !state) {
    return (
      <div className={styles.overlay}>
        <div className={styles.errorRow} role="alert">
          Couldn’t load your profile: {loadError}
        </div>
      </div>
    );
  }

  const stepId = FRONT_DOOR_STEP_IDS[stepIndex];
  const { profile } = state;
  // W2/W1 read-side resolution — see each function's own doc comment
  // (lib/frontDoorCards.ts) for why this can't just be profile.workRights /
  // profile.attributes.relocationPosture read verbatim.
  const workRightsSelection = resolveWorkRightsSelection(profile);
  const relocationPostures = resolveRelocationPostures(profile.attributes);

  return (
    <div className={styles.overlay}>
      <div className={styles.topRow}>
        <div
          className={styles.dots}
          role="progressbar"
          aria-valuenow={stepIndex + 1}
          aria-valuemin={1}
          aria-valuemax={TOTAL_FRONT_DOOR_STEPS}
        >
          {FRONT_DOOR_STEP_IDS.map((id, i) => (
            <span
              key={id}
              className={[
                styles.dot,
                i === stepIndex ? styles.dotActive : styles.dotInactive,
                i <= stepIndex ? styles.dotFilled : styles.dotEmpty,
              ].join(' ')}
            />
          ))}
        </div>
        <button type="button" className={styles.skipButton} onClick={skipAll} disabled={finishing}>
          Skip
        </button>
      </div>

      {preview ? (
        <div className={styles.previewBanner}>
          Preview — your answers save to your profile; the first-login gate is not stamped.
        </div>
      ) : null}

      {stepIndex > 0 ? (
        <button type="button" className={styles.backButton} onClick={goBack}>
          ← Back
        </button>
      ) : null}

      {stepId === 'preferred_name' ? (
        <StepFrame
          title="What should Meira call you?"
          skippedBadge={skippedSteps.has('preferred_name') && !profile.preferredName}
          onSkip={() => skipStep('preferred_name')}
        >
          <PreferredNameCard
            value={profile.preferredName}
            suggestion={suggestedPreferredName}
            saving={saving}
            onSave={async (name) => {
              await saveCard({ preferredName: name });
              advance();
            }}
          />
        </StepFrame>
      ) : null}

      {stepId === 'home_country' ? (
        <StepFrame
          title="Confirm your home country"
          subtitle="This sets the honest default for spelling, résumé format, and other conventions."
          skippedBadge={skippedSteps.has('home_country') && !profile.declaredHomeCountry}
          onSkip={() => skipStep('home_country')}
        >
          <HomeCountryCard
            value={profile.declaredHomeCountry}
            suggestion={state.suggestedHomeCountry}
            saving={saving}
            onSave={async (code) => {
              await saveCard({ homeCountry: code });
              advance();
            }}
          />
        </StepFrame>
      ) : null}

      {stepId === 'postal_radius' ? (
        <StepFrame
          title="Where's your home base?"
          subtitle="Lets us keep local and 100%-remote roles within reach."
          skippedBadge={skippedSteps.has('postal_radius') && !profile.postalCode}
          onSkip={() => skipStep('postal_radius')}
        >
          <PostalRadiusCard
            postalCode={profile.postalCode}
            radiusMiles={profile.radiusMiles}
            saving={saving}
            onSave={async ({ postalCode, radiusMiles }) => {
              await saveCard({ postalCode, radiusMiles });
              advance();
            }}
          />
        </StepFrame>
      ) : null}

      {stepId === 'work_rights' ? (
        <StepFrame
          title="Work rights anywhere else?"
          skippedBadge={
            skippedSteps.has('work_rights') &&
            workRightsSelection.selected.length === 0 &&
            !workRightsSelection.noneDeclared
          }
          onSkip={() => skipStep('work_rights')}
        >
          <WorkRightsCard
            selected={workRightsSelection.selected}
            noneDeclared={workRightsSelection.noneDeclared}
            saving={saving}
            onSave={async (answer) => {
              await saveCard(buildWorkRightsPatch(answer));
              advance();
            }}
          />
        </StepFrame>
      ) : null}

      {stepId === 'relocation' ? (
        <StepFrame
          title="Open to relocation?"
          skippedBadge={skippedSteps.has('relocation') && relocationPostures.length === 0}
          onSkip={() => skipStep('relocation')}
        >
          <RelocationCard
            postures={relocationPostures}
            targets={profile.targetMarkets}
            saving={saving}
            onSave={async ({ postures, targets }) => {
              await saveCard({
                relocationPostures: postures,
                targetMarkets: targets,
                targetGeoIds: deriveTargetGeoIds(targets),
              });
              advance();
            }}
          />
        </StepFrame>
      ) : null}

      {stepId === 'contact' ? (
        <StepFrame
          title="How do we reach you?"
          skippedBadge={skippedSteps.has('contact') && !profile.attributes.phone}
          onSkip={() => skipStep('contact')}
        >
          <ContactCard
            email={state.email ?? user?.email ?? ''}
            phone={profile.attributes.phone ?? null}
            saving={saving}
            onSave={async (phone) => {
              await saveCard({ phone });
              advance();
            }}
          />
        </StepFrame>
      ) : null}

      {stepId === 'finale' ? (
        <StepFrame
          title="Let's seed your corpus"
          onSkip={() => void finishFlow()}
          skipLabel="Skip — I’ll add this later"
        >
          <FinaleCard
            seeded={finaleSeeded}
            resultNote={finaleResultNote}
            saving={finishing}
            onImported={handleFinaleImported}
            onFinish={() => void finishFlow()}
          />
        </StepFrame>
      ) : null}

      {saveError ? (
        <div className={styles.errorRow} role="alert">
          Couldn’t save that: {saveError}
        </div>
      ) : null}
    </div>
  );
}
